// The club's name, as a way through to the club.
//
// Every screen that names a host uses this, so the name looks and behaves the
// same wherever it appears — bold and underlined, which is what says "this is a
// link" without a coloured word competing with the event's own title.
//
// A club with no socials filled in renders as plain text rather than a control
// that opens an empty box. That is the common case early on, and a dead link is
// worse than no link.
import { useState, useEffect } from 'react';
import { safeHref } from '../utils/safeUrl.js';

/** In the order a student is likely to want them. */
const PLATFORMS = [
  { key: 'instagram', label: 'Instagram' },
  { key: 'discord', label: 'Discord' },
  { key: 'website', label: 'Website' },
  { key: 'facebook', label: 'Facebook' },
  { key: 'linkedin', label: 'LinkedIn' },
];

/** Drawn marks, not emoji — an emoji renders as a different picture per device. */
function SocialIcon({ name }) {
  const p = {
    instagram: (
      <>
        <rect x="3" y="3" width="18" height="18" rx="5" />
        <circle cx="12" cy="12" r="4" />
        <circle cx="17.2" cy="6.8" r="1.2" fill="currentColor" stroke="none" />
      </>
    ),
    facebook: <path d="M14.5 8.5h2.2V5.6h-2.6c-2.2 0-3.4 1.3-3.4 3.4v1.6H8.4v2.9h2.3V21h3v-7.5h2.3l.4-2.9h-2.7V9.6c0-.7.3-1.1.8-1.1z" fill="currentColor" stroke="none" />,
    linkedin: (
      <>
        <rect x="3" y="3" width="18" height="18" rx="3" />
        <circle cx="7.4" cy="7.6" r="1.3" fill="currentColor" stroke="none" />
        <path d="M6.5 10.6v7M11 17.5v-4a2.2 2.2 0 0 1 4.4 0v4M11 10.6v1.2" />
      </>
    ),
    discord: (
      <>
        <path d="M8.3 17.6c-1.9-.5-3.3-1.6-3.3-1.6.3-3.6 1.6-6.6 1.6-6.6A9.4 9.4 0 0 1 9.8 8l.5 1a12 12 0 0 1 3.4 0l.5-1a9.4 9.4 0 0 1 3.2 1.4s1.3 3 1.6 6.6c0 0-1.4 1.1-3.3 1.6l-.8-1.3" />
        <circle cx="9.6" cy="13" r="1.1" fill="currentColor" stroke="none" />
        <circle cx="14.4" cy="13" r="1.1" fill="currentColor" stroke="none" />
      </>
    ),
    website: (
      <>
        <circle cx="12" cy="12" r="8.5" />
        <path d="M3.5 12h17M12 3.5c2.4 2.6 2.4 14.4 0 17M12 3.5c-2.4 2.6-2.4 14.4 0 17" />
      </>
    ),
  }[name];
  if (!p) return null;
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor"
         strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {p}
    </svg>
  );
}

/** A drawn pin, for the club's own room. Same stroke weight as the platforms. */
function RoomIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor"
         strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 21.5s6.5-6 6.5-11a6.5 6.5 0 1 0-13 0c0 5 6.5 11 6.5 11z" />
      <circle cx="12" cy="10.5" r="2.4" />
    </svg>
  );
}

/** Does this club have anything to show? */
export function hasSocials(socials) {
  // safeHref as well as the server's socialUrl: a profile saved before that
  // rule existed is served exactly as stored.
  return PLATFORMS.some(p => safeHref(socials?.[p.key]));
}

/**
 * The route to a club's own room, on the pathfinder.
 *
 * `?b=&r=` is the pathfinder's own room deep link, and it wants BOTH — a
 * building on its own is not enough for it to open a route, so a room stored
 * without a number is shown as text rather than as a link that lands on a
 * search box.
 */
export function clubRoomHref(room) {
  if (!room?.building || !room?.roomNumber) return '';
  const p = new URLSearchParams({ b: room.building, r: room.roomNumber });
  return `/umanitoba?${p.toString()}`;
}

/** "Room 390 · EITC" — what the club room row says under the heading. */
function roomLine(room) {
  const head = room?.label || (room?.roomNumber ? `Room ${room.roomNumber}` : '');
  return [head, room?.buildingName].filter(Boolean).join(' · ');
}

/** Is there a room worth printing? */
export function hasClubRoom(room) {
  return !!(room && (room.roomNumber || room.buildingName));
}

/**
 * The club name. A button when there is somewhere to go, plain text otherwise.
 *
 * A club room counts as somewhere to go, exactly like a social does — a club
 * with a room and no Instagram still has something a student wants, and it
 * would be the one thing hidden behind an unclickable name.
 *
 * `stop` matters: on the attendee page the name sits inside the banner, and the
 * banner is itself a control that opens the details. Without stopping the event
 * here, following the club would also unroll the page underneath the popup.
 */
export function ClubName({ name, socials, room, className = '' }) {
  const [open, setOpen] = useState(false);
  if (!name) return null;
  if (!hasSocials(socials) && !hasClubRoom(room)) return <span className={className}>{name}</span>;
  return (
    <>
      <button className={`cs-name ${className}`} title={`About ${name}`}
              onClick={e => { e.stopPropagation(); setOpen(true); }}>
        {name}
      </button>
      {open && <ClubSocials name={name} socials={socials} room={room} onClose={() => setOpen(false)} />}
    </>
  );
}

/** The popup itself. */
export function ClubSocials({ name, socials, room, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const rows = PLATFORMS.filter(p => safeHref(socials?.[p.key]));
  const showRoom = hasClubRoom(room);
  const href = showRoom ? clubRoomHref(room) : '';
  // The room leads, above the socials. Somebody who has opened a club from an
  // event is standing on campus; "where are they" is a question this building
  // can answer now, and following an Instagram is a question for later.
  const roomBody = showRoom && (
    <>
      <RoomIcon />
      <span className="cs-roomtext">
        <b>Club room</b>
        <i>{roomLine(room)}</i>
      </span>
      {href && <span className="cs-go" aria-hidden="true">→</span>}
    </>
  );
  return (
    <div className="cs-scrim" onClick={e => { e.stopPropagation(); onClose(); }}>
      <div className="cs-box" onClick={e => e.stopPropagation()} role="dialog" aria-label={`About ${name}`}>
        <button className="cs-x" onClick={onClose} aria-label="Close">✕</button>
        <h3 className="cs-h">{name}</h3>
        <div className="cs-list">
          {showRoom && (href
            ? <a className="cs-row cs-room" href={href}>{roomBody}</a>
            // A room we cannot route to is still an answer — it just is not a
            // link, and a dead link is worse than a printed address.
            : <div className="cs-row cs-room cs-flat">{roomBody}</div>)}
          {rows.map(p => (
            <a key={p.key} className="cs-row" href={safeHref(socials[p.key])}
               target="_blank" rel="noopener noreferrer">
              <SocialIcon name={p.key} />
              <span>{p.label}</span>
              <span className="cs-go" aria-hidden="true">↗</span>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}

export const CLUB_SOCIALS_CSS = `
/* Bold and underlined: it has to read as a way through without a coloured word
   competing with the event's own title beside it. */
.cs-name { background:none; border:none; padding:0; font:inherit; font-weight:800;
  text-decoration:underline; text-underline-offset:2px; text-decoration-thickness:1.5px;
  color:inherit; cursor:pointer; }
.cs-name:hover { text-decoration-thickness:2.5px; }

.cs-scrim { position:fixed; inset:0; z-index:120; background:rgba(15,18,23,.5);
  display:flex; align-items:center; justify-content:center; padding:16px;
  animation:cs-fade .16s ease; }
@keyframes cs-fade { from { opacity:0; } to { opacity:1; } }
.cs-box { position:relative; background:#fff; color:#15181d; width:min(360px,100%);
  border-radius:16px; padding:20px; box-shadow:0 18px 50px rgba(15,18,23,.34);
  animation:cs-pop .2s cubic-bezier(.22,1,.36,1);
  font:15px/1.5 -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; }
@keyframes cs-pop { from { opacity:0; transform:translateY(8px) scale(.98); } to { opacity:1; transform:none; } }
@media (prefers-reduced-motion: reduce) { .cs-scrim, .cs-box { animation:none; } }
.cs-x { position:absolute; top:12px; right:12px; border:none; background:#f1f2f6;
  width:28px; height:28px; border-radius:50%; font-size:13px; cursor:pointer; }
.cs-h { margin:0 30px 12px 0; font-size:18px; font-weight:800; }
.cs-list { display:flex; flex-direction:column; gap:6px; }
.cs-row { display:flex; align-items:center; gap:10px; padding:10px 12px; border-radius:10px;
  border:1.5px solid #e2e5ea; color:inherit; text-decoration:none; font-size:14.5px;
  font-weight:600; }
.cs-row:hover { border-color:#4f46e5; background:#f7f7ff; }
.cs-go { margin-left:auto; color:#6b7280; font-weight:400; }

/* The club room, above the socials and picked out from them: it is the one row
   that goes somewhere on this campus rather than off to another site, and the
   arrow says so (→, not ↗). */
.cs-room { border-color:#c7c9f5; background:#f7f7ff; align-items:flex-start; }
.cs-room:hover { background:#eef0ff; }
.cs-roomtext { display:flex; flex-direction:column; min-width:0; }
.cs-roomtext b { font-size:12px; font-weight:800; text-transform:uppercase; letter-spacing:.05em;
  color:#4f46e5; }
.cs-roomtext i { font-style:normal; font-size:14.5px; font-weight:600; }
.cs-flat { cursor:default; }
.cs-flat:hover { border-color:#c7c9f5; background:#f7f7ff; }
`;
